import api from "./api";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

const ORDERS_BASE = "/api/v1/orders";

export const STATUS_TABS = [
  { key: "all", label: "All Orders" },
  { key: "pending", label: "Pending" },
  { key: "in_process", label: "In Process" },
  { key: "rejected", label: "Rejected" },
  { key: "dispatched", label: "Dispatched" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
];

/** @deprecated use STATUS_TABS */
export const SELLER_TABS = STATUS_TABS;

export const OUTBOUND_TABS = [
  { key: "qc_pending", label: "QC Approval Pending" },
  { key: "dispatch", label: "Dispatch" },
  { key: "dispatched", label: "Dispatched" },
];

export const MARKETPLACE_OPTIONS = [
  { value: "", label: "All Marketplaces" },
  { value: "ebay", label: "eBay" },
  { value: "shopify", label: "Shopify" },
  { value: "vinted", label: "Vinted" },
  { value: "poshmark", label: "Poshmark" },
];

export function getUserId(user) {
  return user?.id ?? user?.user_id ?? user?.userId ?? null;
}

export function resolveApiRole(userRole, options = {}) {
  const role = String(userRole || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");

  if (role === "owner" || role === "admin") return "admin";
  if (role === "outbound") {
    if (options.outboundTab === "qc_pending") return "outbound_qc";
    if (options.outboundTab === "dispatch") return "packed";
    return "admin";
  }
  return role;
}

function getOrderDate(order) {
  const raw =
    order.created_at || order.processing_updated_at || order.updated_at;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export async function fetchTrackingHistory(trackingId) {
  const res = await fetch(`${API_HOST}/api/track/${trackingId}`);
  if (!res.ok) throw new Error("Failed to fetch tracking history");
  const json = await res.json();
  return json.data; // { trackHistory, invoice }
}

export function isOrderDelivered(order) {
  const status = String(order?.delivered_status || "").toLowerCase();
  return status === "delivered" || status === "yes" || status === "complete";
}

export function isOrderCancelled(order) {
  const status = String(order?.cancelled_status || "").toLowerCase();
  if (["cancelled", "canceled", "yes"].includes(status)) return true;

  const mpCancel = String(order?.marketplace_cancel_status || "").toLowerCase();
  if (!mpCancel) return false;
  if (mpCancel.includes("none")) return false;
  return (
    mpCancel.includes("cancel") ||
    mpCancel.includes("requested") ||
    mpCancel.includes("approved")
  );
}

export function isOrderDispatched(order) {
  return String(order?.outbound_status || "").toLowerCase() === "dispatched";
}

export function applyOrderFilters(orders, filters = {}) {
  const { dateFrom, dateTo, marketplace, search } = filters;
  let result = orders || [];

  if (dateFrom) {
    const from = new Date(`${dateFrom}T00:00:00`);
    result = result.filter((order) => {
      const d = getOrderDate(order);
      return d && d >= from;
    });
  }

  if (dateTo) {
    const to = new Date(`${dateTo}T23:59:59.999`);
    result = result.filter((order) => {
      const d = getOrderDate(order);
      return d && d <= to;
    });
  }

  if (marketplace) {
    const mp = marketplace.toLowerCase();
    result = result.filter((order) => {
      const value = String(
        order.marketplace || order.source || "",
      ).toLowerCase();
      return value.includes(mp);
    });
  }

  if (search?.trim()) {
    const q = search.trim().toLowerCase();
    result = result.filter((order) => {
      const idMatch = String(order.id).includes(q);
      const marketplaceMatch = String(order.marketplace || order.source || "")
        .toLowerCase()
        .includes(q);
      const trackingMatch = String(order.courier_tracking_id || "")
        .toLowerCase()
        .includes(q);
      const itemMatch = (order.items || []).some(
        (item) =>
          String(item.sku || "")
            .toLowerCase()
            .includes(q) ||
          String(item.title || "")
            .toLowerCase()
            .includes(q),
      );
      return idMatch || marketplaceMatch || trackingMatch || itemMatch;
    });
  }

  return result;
}

export function filterStatusTabOrders(orders, tab, userId = null) {
  const uid = userId != null ? Number(userId) : null;

  return (orders || []).filter((order) => {
    if (uid != null) {
      const hasMyItems = (order.items || []).some(
        (item) => Number(item.uploaded_by) === uid,
      );
      if (!hasMyItems) return false;
    }

    const sellerStatus = order.seller_status;
    const qcStatus = order.qc_status;
    const outboundStatus = order.outbound_status;

    switch (tab) {
      case "all":
        return true;
      case "pending":
        return (
          sellerStatus === "pending" &&
          !isOrderDelivered(order) &&
          !isOrderCancelled(order)
        );
      case "in_process":
        return (
          (sellerStatus === "ready_to_pick" ||
            order.rider_status === "picked" ||
            qcStatus === "approved" ||
            outboundStatus === "packed") &&
          qcStatus !== "rejected" &&
          sellerStatus !== "not_available" &&
          outboundStatus !== "dispatched" &&
          !isOrderDelivered(order) &&
          !isOrderCancelled(order)
        );
      case "rejected":
        return (
          (sellerStatus === "not_available" || qcStatus === "rejected") &&
          !isOrderCancelled(order)
        );
      case "dispatched":
        return (
          isOrderDispatched(order) &&
          !isOrderDelivered(order) &&
          !isOrderCancelled(order)
        );
      case "delivered":
        return isOrderDelivered(order);
      case "cancelled":
        return isOrderCancelled(order);
      default:
        return true;
    }
  });
}

/** @deprecated use filterStatusTabOrders */
export function filterSellerTabOrders(orders, tab, userId) {
  return filterStatusTabOrders(orders, tab, userId);
}

export function filterOutboundTabOrders(orders, tab) {
  return (orders || []).filter((order) => {
    const qcStatus = order.qc_status;
    // eslint-disable-next-line
    const outboundStatus = order.outbound_status;
    const riderStatus = order.rider_status;

    switch (tab) {
      case "qc_pending":
        return riderStatus === "picked" && qcStatus === "pending";
      case "dispatch":
        return (
          qcStatus === "approved" &&
          !isOrderDispatched(order) &&
          !isOrderDelivered(order) &&
          !isOrderCancelled(order)
        );
      case "dispatched":
        return (
          isOrderDispatched(order) &&
          !isOrderDelivered(order) &&
          !isOrderCancelled(order)
        );
      default:
        return true;
    }
  });
}

export function filterRiderTabOrders(orders, tab = "pending") {
  return (orders || []).filter((order) => {
    switch (tab) {
      case "pending":
        return (
          order.seller_status === "ready_to_pick" &&
          order.rider_status === "pending"
        );
      case "picked":
        return order.rider_status === "picked";
      default:
        return true;
    }
  });
}

export async function fetchOrders({
  companyId,
  userId,
  userRole,
  statusTab,
  outboundTab,
  riderTab,
}) {
  const role = String(userRole || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");

  let apiRole = resolveApiRole(role, { outboundTab });

  if (role === "seller" && statusTab && statusTab !== "pending") {
    apiRole = "admin";
  }

  if (role === "outbound" && outboundTab === "dispatched") {
    apiRole = "admin";
  }

  if (role === "rider" && riderTab && riderTab !== "pending") {
    apiRole = "admin";
  }

  const params = {
    company_id: companyId,
    user_id: userId,
    role: apiRole,
  };

  const res = await api.get(ORDERS_BASE, { params });
  const payload = res.data;

  if (payload?.success === false) {
    throw new Error(
      payload.error || payload.message || "Failed to load orders",
    );
  }

  let orders = payload?.data || [];

  if (role === "seller" && statusTab) {
    orders = filterStatusTabOrders(orders, statusTab, userId);
  }

  if ((role === "admin" || role === "owner") && statusTab) {
    orders = filterStatusTabOrders(orders, statusTab);
  }

  if (role === "outbound" && outboundTab) {
    orders = filterOutboundTabOrders(orders, outboundTab);
  }

  if (role === "rider") {
    orders = filterRiderTabOrders(orders, riderTab || "pending");
  }

  return {
    total: orders.length,
    data: orders,
  };
}

export async function updateOrderStatus({
  orderId,
  companyId,
  userId,
  action,
  remarks = null,
  courierTrackingId = null,
}) {
  const body = {
    company_id: companyId,
    user_id: userId,
    action,
  };

  if (remarks) body.remarks = remarks;
  if (courierTrackingId) body.courier_tracking_id = courierTrackingId;

  const res = await api.put(`${ORDERS_BASE}/${orderId}/status`, body);
  const payload = res.data;

  if (payload?.success === false) {
    throw new Error(
      payload.error || payload.message || "Failed to update order",
    );
  }

  return payload;
}
